import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type { ColorScheme }

const tokens = {
  dark: {
    border: 'border.subtle',
    label: 'text.secondary',
    icon: 'icon.secondary',
    value: 'text.primary',
    hoverBg: 'bg.table-row-hover',
    selectedBg: 'bg.card-elevated',
  },
  light: {
    border: 'light.border.subtle',
    label: 'light.text.secondary',
    icon: 'light.icon.secondary',
    value: 'light.text.primary',
    hoverBg: 'light.bg.table-row-hover',
    // Light card-elevated is white like the card itself, so it wouldn't show.
    selectedBg: 'light.bg.table-row-selected',
  },
} as const

export const statTileStyles = (
  colorScheme: ColorScheme = 'dark',
  isInteractive = false,
  isSelected = false
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minW: 0,
  p: '16px',
  textAlign: 'left',
  w: 'full',
  ...(isInteractive && {
    // The tile renders as a native <button>. Chakra's preflight resets it
    // today, but the package config doesn't promise a global reset, so the
    // tile clears native button chrome itself (same as FeedItem).
    // StatTileGroup's dividers use a more specific selector and still apply.
    WebkitAppearance: 'none',
    appearance: 'none',
    bg: 'transparent',
    border: 'none',
    color: 'inherit',
    font: 'inherit',
    m: 0,
    cursor: 'pointer',
    _hover: { bg: tokens[colorScheme].hoverBg },
    ...(isSelected && {
      bg: tokens[colorScheme].selectedBg,
      _hover: { bg: tokens[colorScheme].selectedBg },
    }),
  }),
})

export const statTileLabelRowStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  alignItems: 'center',
  color: tokens[colorScheme].label,
  display: 'flex',
  gap: '8px',
  textStyle: 'caption',
})

export const statTileIconStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  alignItems: 'center',
  color: tokens[colorScheme].icon,
  display: 'inline-flex',
  flexShrink: 0,
  justifyContent: 'center',
  h: '16px',
  w: '16px',
})

export const statTileValueRowStyles: SystemStyleObject = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
}

export const statTileValueStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: tokens[colorScheme].value,
  fontVariantNumeric: 'tabular-nums',
  textStyle: 'title',
})

export const statTileSubLabelStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: tokens[colorScheme].label,
  textStyle: 'caption',
})

/**
 * Bordered grid with 1px dividers between tiles — vertical between columns,
 * horizontal between rows.
 */
export const statTileGroupStyles = (
  colorScheme: ColorScheme = 'dark',
  columns: number
): SystemStyleObject =>
  // nth-child, not nth-of-type: clickable tiles are <button>s and the rest
  // <div>s, and position must count every grid item regardless of tag.
  // Computed selectors widen to a string index signature, which Chakra's
  // SystemStyleObject can't express; the shape is still valid.
  ({
    border: '1px solid',
    borderColor: tokens[colorScheme].border,
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    [`& > *:not(:nth-child(${columns}n + 1))`]: {
      borderLeft: '1px solid',
      borderLeftColor: tokens[colorScheme].border,
    },
    [`& > *:nth-child(n + ${columns + 1})`]: {
      borderTop: '1px solid',
      borderTopColor: tokens[colorScheme].border,
    },
  } as SystemStyleObject)
